import { Copy } from "lucide-react";
import type { ReactNode } from "react";
import { queue } from "@/components/Toast";
import PlatformGlyph, { platformLabel } from "@/app/routes/dashboard/components/PlatformGlyph";
import type { Distribution, DistributionEntry } from "@/types/productionBrief.type";
import { joinNames } from "./briefParts";
import { BriefSection, SectionHead, type BriefAudience } from "./BriefPrimitives";

async function copyCaption(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    queue.add({ title: "Caption copied", variant: "success" });
  } catch {
    queue.add({ title: "Could not copy the caption", variant: "error" });
  }
}

function hasCopy(entry: DistributionEntry | undefined): entry is DistributionEntry {
  return !!entry && !!(
    entry.caption?.trim() ||
    entry.title?.trim() ||
    entry.description?.trim() ||
    entry.hashtags?.length ||
    entry.tags?.length
  );
}

/**
 * The channels with copy, in the brief's order. One caption per channel and
 * none for YouTube (Figma 08, CEO note): its title and description are set
 * where it posts, on the Live tab.
 */
export function captionEntries(distribution?: Distribution): Array<[string, DistributionEntry]> {
  return Object.entries(distribution ?? {}).filter(
    (pair): pair is [string, DistributionEntry] => pair[0] !== "YOUTUBE" && hasCopy(pair[1]),
  );
}

function CopyBlock({ label, text, copyable }: { label: string; text?: string; copyable?: boolean }) {
  const value = text?.trim();
  return (
    <div>
      <div className="flex h-6 items-center justify-between gap-2">
        <p className="text-[12px] text-co-muted">{label}</p>
        {copyable && value ? (
          <button
            type="button"
            onClick={() => void copyCaption(value)}
            aria-label={`Copy ${label.toLowerCase()}`}
            title="Copy"
            className="grid size-6 place-items-center rounded-pill text-co-muted transition-colors hover:text-co-text focus-visible:outline focus-visible:outline-1 focus-visible:outline-co-accent"
          >
            <Copy className="size-3.5" strokeWidth={1.8} />
          </button>
        ) : null}
      </div>
      {value ? (
        // plaintext bidi: a multilingual caption sets each paragraph's own direction.
        <p dir="auto" className="mt-1 whitespace-pre-wrap text-[14px] leading-[1.45] text-co-text [unicode-bidi:plaintext]">
          {value}
        </p>
      ) : (
        <p className="mt-1 text-[14px] leading-[1.45] text-co-muted">Not written yet</p>
      )}
    </div>
  );
}

/**
 * One channel's caption (Calendar / Brief caption card): the channel, then
 * Caption with its copy button. A multilingual brand's languages all sit in
 * the one caption.
 */
export function BriefCaptionCard({
  platform,
  name,
  entry,
}: {
  /** Null for the static brief's master caption, which has no channel. */
  platform: string | null;
  name: string;
  entry: DistributionEntry;
}) {
  const isYouTube = platform === "YOUTUBE";
  const tags = isYouTube ? entry.tags ?? [] : entry.hashtags ?? [];
  const tagLine = tags
    .map((tag) => tag.trim())
    .filter(Boolean)
    .map((tag) => (isYouTube ? tag : `#${tag.replace(/^#/, "")}`))
    .join(isYouTube ? ", " : " ");
  const copy: ReactNode = isYouTube ? (
    <>
      {entry.title?.trim() ? <CopyBlock label="Title" text={entry.title} /> : null}
      <CopyBlock label="Description" text={entry.description} copyable />
    </>
  ) : (
    <CopyBlock label="Caption" text={entry.caption} copyable />
  );
  return (
    <article className="flex min-w-0 flex-col rounded-card bg-co-surface px-4 pb-6 pt-3.5">
      <header className="flex items-center gap-2">
        {platform ? <PlatformGlyph platform={platform} className="size-4 shrink-0 text-co-muted" /> : null}
        <span className="text-[14px] font-semibold text-co-text">{name}</span>
      </header>
      <div className="mt-2.5 flex flex-col gap-3">
        {copy}
        {tagLine ? <p className="text-[13px] leading-snug text-co-muted">{tagLine}</p> : null}
      </div>
    </article>
  );
}

export function BriefCaptions({
  distribution,
  masterCaption,
  children,
}: {
  distribution?: Distribution;
  audience?: BriefAudience;
  /** Static briefs: the body's own caption, shown only when no channel carries copy. */
  masterCaption?: { caption?: string };
  /** Anything that belongs under the cards (the static brief's alt text). */
  children?: ReactNode;
}) {
  const entries = captionEntries(distribution);
  const master =
    !entries.length && masterCaption?.caption?.trim()
      ? masterCaption
      : null;
  if (!entries.length && !master && !children) return null;
  const names = entries.map(([platform]) => platformLabel(platform));
  return (
    <BriefSection accent="amber">
      <SectionHead accent="amber" title="Post captions" aside={names.length ? joinNames(names) : null} tight />
      {entries.length || master ? (
        <div className="grid grid-cols-1 gap-3 @[36rem]/briefdoc:grid-cols-2 @[36rem]/briefdoc:gap-5">
          {entries.map(([platform, entry], i) => (
            <BriefCaptionCard key={platform} platform={platform} name={names[i]!} entry={entry} />
          ))}
          {master ? (
            <BriefCaptionCard
              platform={null}
              name="Caption"
              entry={{ caption: master.caption ?? "", hashtags: [] }}
            />
          ) : null}
        </div>
      ) : null}
      {children}
    </BriefSection>
  );
}
